import { flow, Instance, types } from 'mobx-state-tree';
import { getBaseStore } from './BaseStore';
import { getEnvironment } from './utils';
import { Datalist } from 'glamorous';
import { Match } from './models/Match';

export const MatchStore = types
  .compose(
    getBaseStore<Instance<typeof Match>, Instance<typeof Match>>(Match, 'constraintMatchId', 'match'),
    types.model('ScoreStore', {}),
  )
  .views((self) => ({
    getScoreList() {
      return Datalist;
    },
  }))
  .actions((self) => {
    const env = getEnvironment(self);

    const getMatches = flow(function*(data: any) {
      // console.log('get matches');
      const result = yield env.api.get('Planning/getMatches', { params: data });
      if (result != null) {
        self.putInStore(result);
      }
    });

    const getComparedMatches = flow(function*(data: any) {
      // console.log('get compared matches');
      const result = yield env.api.get('Planning/getComparedMatches', { params: data });
      if (result != null) {
        if (self.miscDatas === undefined) self.miscDatas = {};
        self.miscDatas['getComparedMatches'] = result;
      }
    });

    const GetTooCloseDuties = flow(function*(startDate: Date, endDate: Date, min: number, max: number, data: any) {
      // console.log('LAUNCHING GetTooCloseDuties ' + new Date());
      const startDateString = startDate.toISOString().split('T')[0];
      const endDateString = endDate.toISOString().split('T')[0];
      // const min = 7;
      // const max = 50;
      data['addFakeRowsWithStats'] = true;
      const results: any = yield env.api.get(`Pharmacy/GetAbnormalDutyIntervals/${startDateString}/${endDateString}/${min}/${max}/${data.savedPlanningId?data.savedPlanningId:-1}`, {
        params: data,
      });
      if (results != null) {
        if (self.miscDatas === undefined) self.miscDatas = {};
        const headerRow = results.pop();
        self.miscDatas['GetTooCloseDuties'] = results;
        self.miscDatas['GetTooCloseDutiesCount'] = headerRow['ecart'];
        self.miscDatas['GetTooCloseDutiesMaximum'] = headerRow['total'];
        self.miscDatas['GetTooCloseDutiesPercentage'] = ((headerRow['ecart'] / headerRow['total']) * 100).toFixed(2);
        self.miscDatas['GetTooCloseDutiesPerformance'] = (
          100 -
          (headerRow['ecart'] / headerRow['total']) * 100
        ).toFixed(2);
      }
      // console.log('COMPLETED GetTooCloseDuties ' + new Date());
    });

    const GetAbnormalDutyCounts = flow(function*(startDate: Date, endDate: Date, data: any) {
      // console.log('LAUNCHING GetAbnormalDutyCounts ' + new Date());
      const startDateString = startDate.toISOString().split('T')[0];
      const endDateString = endDate.toISOString().split('T')[0];
      data['addFakeRowsWithStats'] = true;
      const results: any = yield env.api.get(`Pharmacy/GetAbnormalDutyCounts/${startDateString}/${endDateString}/${data.offset?data.offset:-1}/${data.savedPlanningId?data.savedPlanningId:-1}/${data.savedPrevPlanningId?data.savedPrevPlanningId:-1}`, {
        params: data,
      });
      if (results != null) {
        if (self.miscDatas === undefined) self.miscDatas = {};
        self.miscDatas['GetAbnormalDutyCountsNumberOfDays'] = results.pop()['dutyCount'];
        self.miscDatas['GetAbnormalDutyCountsMaxForPeriod'] = results.pop()['dutyCount'];
        self.miscDatas['GetAbnormalDutyCountsMinForPeriod'] = results.pop()['dutyCount'];
        self.miscDatas['GetAbnormalDutyCountsNumberOfPharmacies'] = results.pop()['dutyCount'];
        self.miscDatas['GetAbnormalDutyCountsNumberOfLevel3Pharmacies'] = results.pop()['dutyCount'];
        self.miscDatas['GetAbnormalDutyCountsPercentage'] = (
          (results.length / self.miscDatas['GetAbnormalDutyCountsNumberOfPharmacies']) *
          100
        ).toFixed(2);
        self.miscDatas['GetAbnormalDutyCountsLevel3Percentage'] = (
          (results.length / self.miscDatas['GetAbnormalDutyCountsNumberOfLevel3Pharmacies']) *
          100
        ).toFixed(2);
        self.miscDatas['GetAbnormalDutyCountsPerformance'] = (
          100 -
          (results.length / self.miscDatas['GetAbnormalDutyCountsNumberOfPharmacies']) * 100
        ).toFixed(2);
        self.miscDatas['GetAbnormalDutyCountsCount'] = results.length;
        self.miscDatas['GetAbnormalDutyCounts'] = results;
      }
      // console.log('COMPLETED GetAbnormalDutyCounts ' + new Date());
    });

    const GetDutyCountChanges = flow(function*(startDate: Date, endDate: Date, data: any) {
      const startDateString = startDate.toISOString().split('T')[0];
      const endDateString = endDate.toISOString().split('T')[0];
      data['addFakeRowsWithStats'] = true;

      const results: any = yield env.api.get(`Pharmacy/GetDutyCountChanges/${startDateString}/${endDateString}/${data.offset == undefined?-1:data.offset}/${data.status.value}/${data.savedPlanningId?data.savedPlanningId:-1}/${data.savedPrevPlanningId?data.savedPrevPlanningId:-1}`, {
        params: data,
      });
      if (results != null) {
        if (self.miscDatas === undefined) self.miscDatas = {};
        self.miscDatas['GetDutyCountChanges'] = results;
      }
    });

    const GetBlackpointTransgressions = flow(function*(startDate: Date, endDate: Date, data: any) {
      const startDateString = startDate.toISOString().split('T')[0];
      const endDateString = endDate.toISOString().split('T')[0];
      data['addFakeRowsWithStats'] = false;

      const results: any = yield env.api.get(`Pharmacy/GetBlackpointTransgressions/${startDateString}/${endDateString}`, {
        params: data,
      });
      if (results != null) {
        if (self.miscDatas === undefined) self.miscDatas = {};
        self.miscDatas['GetBlackpointTransgressions'] = results;
      }
    });

    const GetGroupTransgressionsByDate = flow(function*(startDate: Date, endDate: Date, planningId: number, group: string, data: any) {
      const startDateString = startDate.toISOString().split('T')[0];
      const endDateString = endDate.toISOString().split('T')[0];
      data['addFakeRowsWithStats'] = false;

      const results: any = yield env.api.get(`Pharmacy/GetGroupTransgressionsByDate/${startDateString}/${endDateString}/${planningId}`, {
        params: data,
      });
      if (results != null) {
        if (self.miscDatas === undefined) self.miscDatas = {};
        self.miscDatas['GetGroupTransgressionsByDate'] = results;
      }
    });

    const GetGroupTransgressions = flow(function*(planningId: number, group: string, data: any) {
      if (planningId === -1) return;
      data['addFakeRowsWithStats'] = false;

      const results: any = yield env.api.get(`Pharmacy/GetGroupTransgressions/${planningId}/${group}`, {
        params: data,
      });
      if (results != null) {
        if (self.miscDatas === undefined) self.miscDatas = {};
        self.miscDatas['GetGroupTransgressions'] = results;
      }
    });

    const GetGroupAverageDutyCounts = flow(function*(startDate: Date, endDate: Date, planningId: number, data: any) {
      const startDateString = startDate.toISOString().split('T')[0];
      const endDateString = endDate.toISOString().split('T')[0];
      data['addFakeRowsWithStats'] = false;

      const results: any = yield env.api.get(`Pharmacy/GetGroupAverageDutyCounts/${startDateString}/${endDateString}/${planningId}`, {
        params: data,
      });
      if (results != null) {
        if (self.miscDatas === undefined) self.miscDatas = {};
        self.miscDatas['GetGroupAverageDutyCounts'] = results;
      }
    });

    const GetUnbalancedNeighbors = flow(function*(startDate: Date, endDate: Date, data: any) {
      const startDateString = startDate.toISOString().split('T')[0];
      const endDateString = endDate.toISOString().split('T')[0];
      data['addFakeRowsWithStats'] = true;
      const results: any = yield env.api.get(`Pharmacy/GetUnbalancedNeighbors/${startDateString}/${endDateString}/${data.offset?data.offset:-1}/${data.savedPlanningId?data.savedPlanningId:-1}/${data.savedPrevPlanningId?data.savedPrevPlanningId:-1}`, {
        params: data,
      });
      if (results != null) {
        if (self.miscDatas === undefined) self.miscDatas = {};
        self.miscDatas['GetUnbalancedNeighborsCount'] = results.pop()['fridays']; // more precise problem counter (6 possible overlap per pharmacy = max 6x nbPharmacies)
        self.miscDatas['GetUnbalancedNeighborsNumberOfPharmacies'] = results.pop()['fridays'];
        self.miscDatas['GetUnbalancedNeighborsNumberOfLevel3Pharmacies'] = results.pop()['fridays'];
        self.miscDatas['GetUnbalancedNeighborsCount'] = results.length; // for now, count as problematic pharmacies vs non-problematic pharmacies
        self.miscDatas['GetUnbalancedNeighborsPercentage'] = (
          (self.miscDatas['GetUnbalancedNeighborsCount'] / self.miscDatas['GetUnbalancedNeighborsNumberOfPharmacies']) *
          100
        ).toFixed(2);
        self.miscDatas['GetUnbalancedNeighborsLevel3Percentage'] = (
          (self.miscDatas['GetUnbalancedNeighborsCount'] /
            self.miscDatas['GetUnbalancedNeighborsNumberOfLevel3Pharmacies']) *
          100
        ).toFixed(2);
        self.miscDatas['GetUnbalancedNeighborsPerformance'] = (
          100 -
          (self.miscDatas['GetUnbalancedNeighborsCount'] / self.miscDatas['GetUnbalancedNeighborsNumberOfPharmacies']) *
            100
        ).toFixed(2);
        self.miscDatas['GetUnbalancedNeighbors'] = results;
      }
    });

    const DetectGroupRuleTransgressions = flow(function*(planningId: number, startDate: Date, endDate: Date) {
      const startDateString = startDate.toDateString().split('T')[0];
      const endDateString = endDate.toDateString().split('T')[0];

      const results: any = yield env.api.get(`Planning/DetectGroupRuleTransgressions/startDate/${startDateString}/endDate/${endDateString}/planningId/${planningId}`, { });
      if (results != null) {
        if (self.miscDatas === undefined) self.miscDatas = {};
        const formatedResults = results.map(item => ({ msg: item }));
        self.miscDatas['DetectGroupRuleTransgressions'] = formatedResults;
      }
    });

    return {
      getMatches,
      getComparedMatches,
      GetTooCloseDuties,
      GetAbnormalDutyCounts,
      GetDutyCountChanges,
      GetBlackpointTransgressions,
      GetGroupTransgressions,
      GetGroupTransgressionsByDate,
      GetGroupAverageDutyCounts,
      GetUnbalancedNeighbors,
      DetectGroupRuleTransgressions
    };
  });

export type MatchStoreType = typeof MatchStore.Type;
